/**
 * @description 管理 object 类型 state 的 Hooks，用法与 class 组件的 this.setState 基本一致。
 * @returns {Array} - [state, setMergeState, setState], setMergeState是合并之前的对象数据，setState是覆盖之前的数据
 */
import { useCallback, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
const isFunction = (value: unknown): value is Function =>
  typeof value === "function";

export type SetState<S extends Record<string, any>> = <K extends keyof S>(
  state: Pick<S, K> | null | ((prevState: Readonly<S>) => Pick<S, K> | S | null)
) => void;

const useSetState = <S extends Record<string, any>>(
  initialState: S | (() => S)
): [S, SetState<S>, Dispatch<SetStateAction<S>>] => {
  const [state, setState] = useState<S>(initialState);

  const setMergeState = useCallback((patch: any) => {
    setState((prevState) => {
      const newState = isFunction(patch) ? patch(prevState) : patch;
      return newState ? { ...prevState, ...newState } : prevState;
    });
  }, []);

  return [state, setMergeState, setState];
};

export default useSetState;
